/**
 * Social login buttons for Google, Apple, and Microsoft.
 * Initiates the OAuth redirect flow via the allauth headless API.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack, Text, HStack, Box } from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { getProviderRedirectURL } from '@/lib/allauth-endpoints';

export function SocialLoginButtons() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const PROVIDERS = [
    {
      id: 'google',
      label: t('social.google'),
      icon: 'G',
      color: '#4285F4',
    },
    {
      id: 'apple',
      label: t('social.apple'),
      icon: '\uF8FF',
      color: '#000000',
    },
    {
      id: 'microsoft',
      label: t('social.microsoft'),
      icon: 'M',
      color: '#00A4EF',
    },
  ] as const;

  const handleSocialLogin = async (providerId: string) => {
    setError('');
    setLoading(providerId);

    try {
      const callbackURL = `${window.location.origin}/auth/callback`;
      const redirectURL = await getProviderRedirectURL(providerId, callbackURL);
      // eslint-disable-next-line react-hooks/immutability -- external OAuth navigation is a legitimate side effect inside an async event handler, not a render-phase mutation
      window.location.href = redirectURL;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('social.failed'),
      );
      setLoading(null);
    }
  };

  return (
    <VStack gap={3} w="100%">
      {PROVIDERS.map((provider) => (
        <Button
          key={provider.id}
          visual="secondary"
          onClick={() => handleSocialLogin(provider.id)}
          disabled={loading !== null}
          style={{ width: '100%' }}
        >
          <HStack gap={3} justify="center">
            <Box
              w="20px"
              h="20px"
              borderRadius="sm"
              bg={provider.color}
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              fontWeight="bold"
            >
              {provider.icon}
            </Box>
            <Text fontSize="sm">
              {loading === provider.id
                ? t('social.redirecting')
                : provider.label}
            </Text>
          </HStack>
        </Button>
      ))}

      {error && (
        <Text color="red.500" fontSize="sm">
          {error}
        </Text>
      )}
    </VStack>
  );
}
